import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { downscaleImage } from '../lib/downscale-image.js';
import { invalidateTaskPhotos } from '../lib/task-photos.js';
import { DocumentLinker } from './DocumentLinker.jsx';
import { TaskPhotosEditor } from './TaskPhotosEditor.jsx';
import { DAYS_OF_WEEK, ROLES } from '../lib/constants.js';

// ─── Task Manager Modal ───────────────────────────────────────────────────────
export function TaskManagerModal({ locations, users, timePeriods, onClose, editTask, onSave, currentUser }) {
  const defaultPeriod = timePeriods[0]?.name || 'Opening';
  const defaultPeriodId = timePeriods[0]?.id || null;

  function initForm(t) {
    const hasPerson = !!(t?.assigned_user_id);
    return {
      title:               t?.title || '',
      description:         t?.description || '',
      time_period:         t?.effective_period || t?.time_period || defaultPeriod,
      time_period_id:      t?.time_period_id || defaultPeriodId,
      due_time:            t?.due_time || '',
      assign_type:         hasPerson ? 'person' : 'role',
      assigned_role:       t?.assigned_role || 'Barista',
      assigned_user_id:    t?.assigned_user_id || '',
      location_id:         t?.location_id || locations[0]?.id || '',
      recurrence:          t?.recurrence || 'daily',
      recurrence_days:     t?.recurrence_days ? JSON.parse(t.recurrence_days) : [],
      priority:            t?.priority ?? 0,
      equipment_id:        t?.equipment_id || '',
      measurement_required: t?.measurement_required || 0,
      sop_title: '', sop_steps: [{ description: '', image_url: '' }],
      sop_link_recipe_id: t?.sop_link_recipe_id || '',
      sop_link_doc_id:    t?.sop_link_doc_id    || '',
    };
  }

  const [form, setForm] = useState(() => initForm(editTask));
  const [saving, setSaving] = useState(false);
  // Reference photos (CJ 2026-09-30). New ones upload after the task saves;
  // savedTaskId makes a retry after a failed upload update the task it
  // already created instead of creating a duplicate.
  const [stagedPhotos, setStagedPhotos] = useState([]);
  const [savedTaskId, setSavedTaskId] = useState(null);
  const [saveErr, setSaveErr] = useState('');
  // Existing photos are edited live, so the task list's thumbnails must
  // refresh however this modal closes — saved or cancelled.
  const touchedLocs = useRef(new Set());
  useEffect(() => { if (form.location_id) touchedLocs.current.add(form.location_id); }, [form.location_id]);
  useEffect(() => () => { touchedLocs.current.forEach((l) => invalidateTaskPhotos(l)); }, []);
  // Declutter ★3: 4 essentials up front (name/period/assign/location);
  // everything else folds behind "More options". Editing expands so
  // existing values are never hidden.
  const [showMore, setShowMore] = useState(!!editTask);
  // Round 30 / Phase C: SOP can be inline steps (current default), a link
  // to a Knowledge Hub recipe, or a link to a knowledge doc.
  const initialSopMode = editTask?.sop_id ? 'inline'
    : editTask?.sop_link_recipe_id ? 'recipe' : editTask?.sop_link_doc_id ? 'doc' : 'none';
  const [sopMode, setSopMode] = useState(initialSopMode);
  // An existing task's built steps load here (request #54: the editor opened
  // every task on "No SOP" with blank steps). Until they arrive the save leaves
  // sop_steps out, so the server keeps the SOP as it is.
  const [sopLoaded, setSopLoaded] = useState(!editTask?.sop_id);
  useEffect(() => {
    if (!editTask?.sop_id) return;
    api('GET', `/api/sop/${editTask.sop_id}`).then((sop) => {
      const steps = (sop.steps || []).map((s) => ({ description: s.description || '', image_url: s.image_url || '' }));
      setForm((f) => ({ ...f, sop_title: sop.title || '', sop_steps: steps.length ? steps : f.sop_steps }));
      setSopLoaded(true);
    }).catch(() => setSaveErr("Couldn't load this task's steps — saving won't change them."));
  }, [editTask?.sop_id]);
  const [locationEquipment, setLocationEquipment] = useState([]);
  const [recipeOptions, setRecipeOptions] = useState([]);
  const [docOptions, setDocOptions] = useState([]);

  useEffect(() => {
    api('GET', '/api/recipes').then((r) => setRecipeOptions(r.recipes || [])).catch(() => {});
    api('GET', '/api/knowledge-docs').then((r) => setDocOptions(r.documents || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.location_id) {
      api('GET', `/api/equipment?location_id=${form.location_id}`)
        .then(d => setLocationEquipment(Array.isArray(d) ? d : []))
        .catch(() => setLocationEquipment([]));
    }
  }, [form.location_id]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function toggleDay(day) {
    setForm(f => {
      const days = f.recurrence_days.includes(day) ? f.recurrence_days.filter(d => d !== day) : [...f.recurrence_days, day];
      return { ...f, recurrence_days: days };
    });
  }

  function setStep(i, field, v) {
    setForm(f => { const s = [...f.sop_steps]; s[i] = { ...s[i], [field]: v }; return { ...f, sop_steps: s }; });
  }
  function addStep() { setForm(f => ({ ...f, sop_steps: [...f.sop_steps, { description: '', image_url: '' }] })); }
  function removeStep(i) { setForm(f => ({ ...f, sop_steps: f.sop_steps.filter((_, j) => j !== i) })); }
  // Shrunk first: an unshrunk phone photo, base64'd into the request, is over
  // the app's 1 MB body limit and the whole save failed (CJ 2026-09-30).
  async function handleStepImage(i, file) {
    if (!file) return;
    const { base64 } = await downscaleImage(file);
    setStep(i, 'image_url', base64);
  }

  async function handleSave() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const recDays = form.recurrence === 'weekly' && form.recurrence_days.length > 0
        ? JSON.stringify(form.recurrence_days) : null;
      const linkedEq = locationEquipment.find(eq => eq.id === Number(form.equipment_id));
      const payload = {
        ...form,
        assigned_role:    form.assign_type === 'role' ? form.assigned_role : null,
        assigned_user_id: form.assign_type === 'person' ? (form.assigned_user_id || null) : null,
        recurrence_days: recDays,
        equipment_id: form.equipment_id || null,
        measurement_required: (form.equipment_id && linkedEq?.requires_temp_log) ? (form.measurement_required ? 1 : 0) : 0,
        sop_steps: !sopLoaded ? undefined : sopMode === 'inline' ? form.sop_steps.filter(s => s.description.trim()) : [],
        sop_title: sopMode === 'inline' ? form.sop_title : '',
        sop_link_recipe_id: sopMode === 'recipe' ? Number(form.sop_link_recipe_id) || null : null,
        sop_link_doc_id:    sopMode === 'doc'    ? Number(form.sop_link_doc_id)    || null : null,
      };
      setSaveErr('');
      let id = editTask?.id || savedTaskId;
      if (id) await api('PUT', `/api/tasks/${id}`, payload);
      else { id = (await api('POST', '/api/tasks', payload)).id; setSavedTaskId(id); }
      if (stagedPhotos.length) {
        try {
          await api('POST', '/api/task-photos', {
            task_id: id, photos: stagedPhotos.map(({ base64, mime, caption }) => ({ base64, mime, caption })),
          });
          setStagedPhotos([]);
        } catch (e) {
          let m = e?.message || String(e); try { m = JSON.parse(m).error || m; } catch (_) { /* plain text */ }
          setSaveErr(`The task is saved, but the photos didn't upload (${m}). Tap Save to try the photos again.`);
          return;
        }
      }
      onSave();
    } catch (e) {
      let m = e?.message || String(e); try { m = JSON.parse(m).error || m; } catch (_) { /* plain text */ }
      setSaveErr(`Couldn't save: ${m}`);
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-cream w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col fade-in shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-crimson rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <h3 className="text-cream font-bold text-lg">{editTask ? 'Edit Task' : 'Create Task'}</h3>
          <button onClick={onClose} className="text-cream/70 hover:text-cream text-3xl leading-none">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Task Name *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30"
              placeholder="e.g. Brew morning drip coffee" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Time Period</label>
              <select value={form.time_period_id != null ? String(form.time_period_id) : (form.time_period === 'Anytime' ? 'anytime' : '')} onChange={e => {
                  if (e.target.value === 'anytime') {
                    setForm(f => ({ ...f, time_period_id: null, time_period: 'Anytime' }));
                    return;
                  }
                  const p = timePeriods.find(x => x.id === parseInt(e.target.value));
                  setForm(f => ({ ...f, time_period_id: p?.id || null, time_period: p?.name || '' }));
                }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none">
                <option value="anytime">Anytime (any time, anyone)</option>
                {timePeriods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Assign To</label>
            <div className="flex gap-2 mb-2">
              {[['role','Role'],['person','Person']].map(([v,l]) => (
                <button key={v} type="button" onClick={() => set('assign_type', v)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${form.assign_type === v ? 'bg-crimson text-white border-crimson' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                  {l}
                </button>
              ))}
            </div>
            {form.assign_type === 'role' ? (
              <select value={form.assigned_role} onChange={e => set('assigned_role', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none">
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            ) : (
              <select value={form.assigned_user_id || ''} onChange={e => set('assigned_user_id', e.target.value ? parseInt(e.target.value) : '')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none">
                <option value="">— Select person —</option>
                {users.filter(u => u.role !== 'inactive').map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Location</label>
            <select value={form.location_id} onChange={e => set('location_id', parseInt(e.target.value))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none">
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <TaskPhotosEditor taskId={editTask?.id || savedTaskId} staged={stagedPhotos} onStagedChange={setStagedPhotos} />

          <button type="button" onClick={() => setShowMore(m => !m)}
            className="w-full text-left text-sm font-medium text-crimson py-1">
            {showMore ? '▾ Fewer options' : '▸ More options — description, due time, recurrence, priority, SOP…'}
          </button>
          {!showMore && (
            <p className="text-xs text-gray-400 -mt-2">Defaults: repeats daily, no due time, no priority.</p>
          )}

          {showMore && (<>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30 resize-none"
              placeholder="What needs to be done..." />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Due Time</label>
            <input value={form.due_time} onChange={e => set('due_time', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none"
              placeholder="e.g. 6:00 AM" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recurrence</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {[['daily','Daily'],['weekly','Weekly'],['monthly','Monthly'],['once','One-time']].map(([val, label]) => (
                <button key={val} onClick={() => set('recurrence', val)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${form.recurrence === val ? 'bg-crimson text-white border-crimson' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                  {label}
                </button>
              ))}
            </div>
            {form.recurrence === 'weekly' && (
              <div className="fade-in">
                <p className="text-xs text-gray-500 mb-2">Select days:</p>
                <div className="flex gap-2 flex-wrap">
                  {DAYS_OF_WEEK.map(day => (
                    <button key={day} onClick={() => toggleDay(day)}
                      className={`w-10 h-10 rounded-full text-xs font-semibold border transition-all ${form.recurrence_days.includes(day) ? 'bg-crimson text-white border-crimson' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Priority</label>
            <div className="flex gap-2 flex-wrap">
              {[[0,'None'],[1,'P1 — Highest'],[2,'P2 — High'],[3,'P3 — Normal'],[4,'P4 — Low'],[5,'P5 — Lowest']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => set('priority', val)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${form.priority === val ? 'bg-crimson text-white border-crimson' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Link to Equipment (Optional)</label>
            <select value={form.equipment_id || ''} onChange={e => { set('equipment_id', e.target.value ? parseInt(e.target.value) : ''); set('measurement_required', 0); }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none">
              <option value="">— No equipment link —</option>
              {locationEquipment.map(eq => <option key={eq.id} value={eq.id}>{eq.name}{eq.requires_temp_log ? ' 🌡' : ''}</option>)}
            </select>
            {form.equipment_id && locationEquipment.find(eq => eq.id === Number(form.equipment_id))?.requires_temp_log ? (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={!!form.measurement_required}
                  onChange={e => set('measurement_required', e.target.checked ? 1 : 0)} className="rounded" />
                <span className="text-sm text-gray-700">Require measurement on completion</span>
              </label>
            ) : null}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">SOP (Optional)</label>
            </div>
            <div className="flex gap-2 mb-3 flex-wrap">
              {[
                ['none', 'No SOP'],
                ['inline', '+ Build steps'],
                ['recipe', '↳ Link recipe'],
                ['doc', '↳ Link doc'],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setSopMode(key)}
                  className={`text-xs px-3 py-1.5 rounded border transition-all ${sopMode === key ? 'bg-crimson text-white border-crimson' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                  {label}
                </button>
              ))}
            </div>
            {sopMode === 'recipe' && (
              <div className="space-y-2 fade-in">
                <select value={form.sop_link_recipe_id}
                  onChange={(e) => set('sop_link_recipe_id', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30">
                  <option value="">— Pick a recipe from the Knowledge Hub —</option>
                  {recipeOptions.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}{r.category ? ` (${r.category})` : ''}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">Staff will see a link in the task that opens the recipe card.</p>
              </div>
            )}
            {sopMode === 'doc' && (
              <div className="space-y-2 fade-in">
                <select value={form.sop_link_doc_id}
                  onChange={(e) => set('sop_link_doc_id', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30">
                  <option value="">— Pick a knowledge doc —</option>
                  {docOptions.map((d) => (
                    <option key={d.id} value={d.id}>{d.title}{d.type ? ` (${d.type})` : ''}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">Staff will see a link in the task that opens the policy / SOP.</p>
              </div>
            )}
            {sopMode === 'inline' && (
              <div className="space-y-3 fade-in">
                <input value={form.sop_title} onChange={e => set('sop_title', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30"
                  placeholder="SOP Title" />
                <div className="space-y-3">
                  {form.sop_steps.map((step, i) => (
                    <div key={i} className="space-y-1.5 bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0">{i+1}.</span>
                        <input value={step.description} onChange={e => setStep(i, 'description', e.target.value)}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
                          placeholder={`Step ${i+1} description`} />
                        {form.sop_steps.length > 1 && (
                          <button onClick={() => removeStep(i)} className="text-red-400 hover:text-red-600 text-lg px-1">×</button>
                        )}
                      </div>
                      <div className="flex gap-2 items-center pl-7">
                        <span className="text-xs text-gray-400">📷</span>
                        <input type="file" accept="image/*" onChange={e => handleStepImage(i, e.target.files[0])}
                          className="flex-1 text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-gray-100 file:text-gray-600 hover:file:bg-gray-200 cursor-pointer" />
                        {step.image_url && (
                          <img src={step.image_url} alt="Preview" className="w-10 h-8 object-cover rounded border border-gray-200 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                  <button onClick={addStep} className="text-sm text-crimson hover:text-crimson-dark font-medium">+ Add Step</button>
                </div>
              </div>
            )}
          </div>

          </>)}

          {editTask && currentUser && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Linked Documents</label>
              <DocumentLinker scope="tasks" id={editTask.id} canEdit={true} userId={currentUser.id} />
            </div>
          )}
        </div>

        {saveErr && <p className="px-5 pt-3 text-xs text-red-600">{saveErr}</p>}
        <div className="p-5 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.title.trim()}
            className="flex-1 py-3 rounded-xl bg-crimson text-white font-semibold hover:bg-crimson-dark disabled:opacity-50 active:scale-98 transition-all">
            {saving ? 'Saving...' : editTask ? 'Save Changes' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
